import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Chip,
  Paper,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Alert,
  CircularProgress,
  Divider,
  Tooltip
} from '@mui/material';
import {
  Share as ShareIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  Group as GroupIcon,
  Person as PersonIcon,
  Check as CheckIcon
} from '@mui/icons-material';
import sharingApi from '../../services/sharingApi';
import teamsApi from '../../services/teamsApi';

interface ShareManagerProps {
  tabId: string;
  tabName: string;
  onShareSuccess?: () => void;
}

interface Team {
  id: number;
  name: string;
  description?: string;
  member_count?: number;
}

const ShareManager: React.FC<ShareManagerProps> = ({ tabId, tabName, onShareSuccess }) => {
  const [emails, setEmails] = useState<string[]>([]);
  const [emailInput, setEmailInput] = useState('');
  const [teams, setTeams] = useState<Team[]>([]);
  const [selectedTeamId, setSelectedTeamId] = useState<number | null>(null);
  const [isSharing, setIsSharing] = useState(false);
  const [shareMessage, setShareMessage] = useState<{ type: 'success' | 'error' | 'info'; text: string } | null>(null);
  const [activeShareType, setActiveShareType] = useState<'email' | 'team'>('email');

  useEffect(() => {
    fetchTeams();
  }, []);

  const fetchTeams = async () => {
    try {
      const teams = await teamsApi.getTeams();
      setTeams(teams);
    } catch (error) {
      console.error('Error fetching teams:', error);
    }
  };

  const isValidEmail = (email: string): boolean => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };

  const handleAddEmail = () => {
    const trimmedEmail = emailInput.trim().toLowerCase();
    if (trimmedEmail && isValidEmail(trimmedEmail) && !emails.includes(trimmedEmail)) {
      setEmails([...emails, trimmedEmail]);
      setEmailInput('');
      setShareMessage(null);
    }
  };

  const handleRemoveEmail = (email: string) => {
    setEmails(emails.filter(e => e !== email));
  };

  const handleShareWithEmails = async () => {
    if (emails.length === 0) return;

    setIsSharing(true);
    setShareMessage(null);

    try {
      const response = await sharingApi.shareWithEmails({
        emails,
        contentId: tabId,
        title: `${tabName} (Shared Tab)`,
        description: `Tab shared from ${tabName}`,
        category: 'Shared',
        tags: ['tab', 'shared']
      });

      if (response.success) {
        const { successCount, addedUsers, failedUsers, alreadySharedUsers } = response.data;
        let messageText = '';
        
        if (successCount > 0) {
          messageText = `Tab added to ${successCount} user${successCount !== 1 ? 's' : ''} dashboard!`;
        }
        
        if (alreadySharedUsers && alreadySharedUsers.length > 0) {
          const alreadyMsg = `${alreadySharedUsers.length} user${alreadySharedUsers.length !== 1 ? 's' : ''} already have this tab`;
          messageText = messageText ? `${messageText} ${alreadyMsg}.` : alreadyMsg;
        }
        
        if (failedUsers && failedUsers.length > 0) {
          const failedMsg = `${failedUsers.length} user${failedUsers.length !== 1 ? 's' : ''} not found`;
          messageText = messageText ? `${messageText} ${failedMsg}.` : failedMsg;
        }
        
        if (!messageText) {
          messageText = 'No changes made';
        }
        
        setShareMessage({
          type: successCount > 0 ? 'success' : 'info',
          text: messageText
        });
        
        if (successCount > 0) {
          setEmails([]);
          onShareSuccess?.();
        }
      } else {
        throw new Error(response.message);
      }
    } catch (error: any) {
      setShareMessage({
        type: 'error',
        text: error.message || 'Failed to share tab'
      });
    } finally {
      setIsSharing(false);
    }
  };

  const handleShareWithTeam = async () => {
    if (!selectedTeamId) return;

    setIsSharing(true);
    setShareMessage(null);

    try {
      const team = teams.find(t => t.id === selectedTeamId);
      const response = await sharingApi.shareWithTeam({
        teamId: selectedTeamId,
        contentId: tabId,
        title: `${tabName} (Team Tab)`,
        description: `Tab shared with team ${team?.name}`,
        category: 'Team',
        tags: ['tab', 'team', 'shared']
      });

      if (response.success) {
        const { successCount, alreadySharedUsers } = response.data;
        let messageText = '';
        
        if (successCount > 0) {
          messageText = `Tab added to ${successCount} team member${successCount !== 1 ? 's' : ''}' dashboard!`;
        }
        
        if (alreadySharedUsers && alreadySharedUsers.length > 0) {
          const alreadyMsg = `${alreadySharedUsers.length} member${alreadySharedUsers.length !== 1 ? 's' : ''} already have this tab`;
          messageText = messageText ? `${messageText} ${alreadyMsg}.` : alreadyMsg;
        }
        
        if (!messageText) {
          messageText = 'All team members already have this tab';
        }
        
        setShareMessage({
          type: successCount > 0 ? 'success' : 'info',
          text: messageText
        });
        
        if (successCount > 0) {
          setSelectedTeamId(null);
          onShareSuccess?.();
        }
      } else {
        throw new Error(response.message);
      }
    } catch (error: any) {
      setShareMessage({
        type: 'error',
        text: error.message || 'Failed to share with team'
      });
    } finally {
      setIsSharing(false);
    }
  };

  return (
    <Box>
      <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
        <ShareIcon color="primary" />
        Share Tab
      </Typography>

      {shareMessage && (
        <Alert severity={shareMessage.type} sx={{ mb: 2 }} onClose={() => setShareMessage(null)}>
          {shareMessage.text}
        </Alert>
      )}

      {/* Share Type Toggle */}
      <Box sx={{ display: 'flex', gap: 1, mb: 3 }}>
        <Button
          variant={activeShareType === 'email' ? 'contained' : 'outlined'}
          startIcon={<PersonIcon />}
          onClick={() => setActiveShareType('email')}
          size="small"
        >
          Share with People
        </Button>
        <Button
          variant={activeShareType === 'team' ? 'contained' : 'outlined'}
          startIcon={<GroupIcon />}
          onClick={() => setActiveShareType('team')}
          size="small"
          disabled={teams.length === 0}
        >
          Share with Team
        </Button>
      </Box>

      {/* Email Sharing */}
      {activeShareType === 'email' && (
        <Paper sx={{ p: 2, backgroundColor: 'background.default' }}>
          <Typography variant="subtitle2" sx={{ mb: 2 }}>
            Add people by email address
          </Typography>
          
          <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
            <TextField
              fullWidth
              size="small"
              label="Email Address"
              value={emailInput}
              onChange={(e) => setEmailInput(e.target.value)}
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  handleAddEmail();
                }
              }}
              placeholder="Enter email address"
              error={emailInput !== '' && !isValidEmail(emailInput)}
              helperText={emailInput !== '' && !isValidEmail(emailInput) ? 'Invalid email format' : ''}
            />
            <Button
              variant="contained"
              onClick={handleAddEmail}
              disabled={!emailInput.trim() || !isValidEmail(emailInput.trim())}
              startIcon={<AddIcon />}
            >
              Add
            </Button>
          </Box>

          {emails.length > 0 && (
            <>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                {emails.map((email) => (
                  <Chip
                    key={email}
                    label={email}
                    onDelete={() => handleRemoveEmail(email)}
                    color="primary"
                  />
                ))}
              </Box>
              
              <Button
                fullWidth
                variant="contained"
                onClick={handleShareWithEmails}
                disabled={isSharing}
                startIcon={isSharing ? <CircularProgress size={20} /> : <ShareIcon />}
              >
                {isSharing ? 'Sharing...' : `Share with ${emails.length} ${emails.length === 1 ? 'Person' : 'People'}`}
              </Button>
            </>
          )}

          {emails.length === 0 && (
            <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
              Add email addresses to share this tab
            </Typography>
          )}
        </Paper>
      )}

      {/* Team Sharing */}
      {activeShareType === 'team' && (
        <Paper sx={{ p: 2, backgroundColor: 'background.default' }}>
          <Typography variant="subtitle2" sx={{ mb: 2 }}>
            Select a team to share with
          </Typography>

          {teams.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
              You are not a member of any teams yet
            </Typography>
          ) : (
            <>
              <List sx={{ mb: 2 }}>
                {teams.map((team) => (
                  <ListItem
                    key={team.id}
                    sx={{
                      border: 1,
                      borderColor: selectedTeamId === team.id ? 'primary.main' : 'divider',
                      borderRadius: 1,
                      mb: 1,
                      cursor: 'pointer',
                      backgroundColor: selectedTeamId === team.id ? 'primary.50' : 'transparent',
                      '&:hover': {
                        backgroundColor: 'action.hover'
                      }
                    }}
                    onClick={() => setSelectedTeamId(team.id)}
                  >
                    <ListItemText
                      primary={team.name}
                      secondary={
                        <>
                          {team.description}
                          {team.member_count && (
                            <Chip 
                              label={`${team.member_count} members`} 
                              size="small" 
                              sx={{ ml: 1 }} 
                            />
                          )}
                        </>
                      }
                    />
                    {selectedTeamId === team.id && (
                      <ListItemSecondaryAction>
                        <CheckIcon color="primary" />
                      </ListItemSecondaryAction>
                    )}
                  </ListItem>
                ))}
              </List>

              <Button
                fullWidth
                variant="contained"
                onClick={handleShareWithTeam}
                disabled={!selectedTeamId || isSharing}
                startIcon={isSharing ? <CircularProgress size={20} /> : <ShareIcon />}
              >
                {isSharing ? 'Sharing...' : selectedTeamId ? `Share with ${teams.find(t => t.id === selectedTeamId)?.name}` : 'Select a Team'}
              </Button>
            </>
          )}
        </Paper>
      )}

      <Divider sx={{ my: 3 }} />

      <Typography variant="caption" color="text.secondary">
        When you share a tab, it will be instantly added to the recipients' dashboards. They'll see it next time they refresh.
      </Typography>
    </Box>
  );
};

export default ShareManager;